import { BehaviorSubject, combineLatest, merge, Observable, ReplaySubject, Subject } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { select, Select, Store } from '@ngxs/store';

import { AdPlatform } from '@app/core/models/common.models';
import {
  COST_PER_MILLE,
  PROFIT,
  RETURN_ON_INVESTMENT,
  REVENUE,
  ROAS,
  VIEW_IMPRESSIONS,
} from '@app/core/models/metrics.constants';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { isTacosMetric } from '@app/key-figures/utilities/key-figures.utilities';
import { lastValue } from '../../../operators/last-value.operator';
import { shareRef } from '../../../operators/share-ref.operator';
import { defaultLayouts, TableLayout, TableLayoutColumn, TableLayoutId } from '../models/table-layout.model';
import { TableLayoutRepositoryService } from './table-layout-repository.service';

@Injectable()
export class TableLayoutService {
  readonly hasVCPMCampaign = select(CustomerState.hasVCPMCampaign);

  @Select(CustomerState.campaignProfitSupported)
  readonly campaignProfitSupported$!: Observable<boolean>;

  @Select(CustomerState.campaignRevenueSupported)
  readonly campaignRevenueSupported$!: Observable<boolean>;

  @Select(CustomerState.platformsAccessible)
  readonly platformsAccessible$!: Observable<AdPlatform[]>;

  private layoutId$ = new ReplaySubject<TableLayoutId>(1);

  update$ = new Subject<TableLayout | undefined>();

  private refresh$ = new BehaviorSubject<void>(void 0);

  private setDefault$ = new Subject<void>();

  readonly needsSaving$: Observable<boolean> = merge(
    this.update$.pipe(map((update) => !!update)),
    this.setDefault$.pipe(map(() => false)),
  ).pipe(startWith(false), shareRef());

  readonly defaultLayout$ = this.layoutId$.pipe(
    distinctUntilChanged(),
    map((layoutId) => defaultLayouts[layoutId]),
    shareRef(),
  );

  readonly currentLayout$: Observable<TableLayout | undefined> = this.layoutId$.pipe(
    distinctUntilChanged(),
    switchMap((layoutId) => this.refresh$.pipe(switchMap(() => this.tlr.getTableLayout(layoutId)))),
    shareRef(),
  );

  readonly storedLayout$: Observable<TableLayout> = combineLatest([this.currentLayout$, this.defaultLayout$]).pipe(
    map(([storedLayout, defaultLayout]) => {
      const storedColumns = storedLayout?.columns || [];
      const defaultColumns = defaultLayout?.columns || [];
      const mergedColumns = [
        ...storedColumns.filter((col) => defaultColumns.some(({ id }) => id === col.id)),
        ...defaultColumns.filter((col) => !storedColumns.some(({ id }) => id === col.id)),
      ];

      return {
        ...storedLayout,
        columns: mergedColumns,
      };
    }),
    shareRef(),
  );

  readonly layout$: Observable<TableLayout> = this.defaultLayout$.pipe(
    switchMap((defaultLayout) =>
      merge(this.storedLayout$, this.update$).pipe(map((layout) => layout || defaultLayout)),
    ),
    switchMap((layout) =>
      combineLatest([this.campaignProfitSupported$, this.campaignRevenueSupported$, this.platformsAccessible$]).pipe(
        map(([hasProfit, hasRevenue, platforms]) => {
          const hasAmazon = platforms.includes('AMAZON');
          const showRevenueROAS = hasRevenue || (hasAmazon && hasProfit);
          return {
            ...layout,
            columns: layout.columns.filter((column) => {
              if (column.id === PROFIT) return false;

              if ([REVENUE, ROAS].includes(column.id)) return showRevenueROAS;

              if (column.id.startsWith(PROFIT) || column.id === RETURN_ON_INVESTMENT) return hasProfit;

              if (isTacosMetric(column.id)) return hasAmazon;

              if ([VIEW_IMPRESSIONS, COST_PER_MILLE].includes(column.id) && !this.hasVCPMCampaign()) return false;

              return true;
            }),
          };
        }),
      ),
    ),
    shareRef(),
  );

  constructor(
    private tlr: TableLayoutRepositoryService,
    private store: Store,
  ) {}

  setLayoutId(layoutId: TableLayoutId): void {
    this.layoutId$.next(layoutId);
  }

  updateColumnLayout(columns: TableLayoutColumn[]): void {
    const layout = {
      ...(lastValue(this.layout$) || {}),
      columns,
    };

    this.update$.next(layout);
  }

  revertToCustom(): void {
    this.update$.next(void 0);
  }

  revertToDefault(): void {
    const layoutId = lastValue(this.layoutId$);

    if (layoutId) {
      this.update$.next(void 0);
      this.tlr
        .removeTableLayout(layoutId)
        .pipe()
        .subscribe(() => this.refresh$.next());
    }
  }

  setAsDefault(): void {
    const layout = lastValue(this.layout$);
    const layoutId = lastValue(this.layoutId$);

    if (layout && layoutId) {
      this.setDefault$.next();

      this.tlr.setTableLayout(layoutId, layout).subscribe(() => this.refresh$.next());
    }
  }
}
